import { Component, OnInit } from '@angular/core';
import {
  UntypedFormControl,
  UntypedFormGroup,
  Validators,
} from '@angular/forms';
import { DateTime as LuxonDateTime } from 'luxon';

import { Client } from 'src/providers/client/client';
import { Schedule } from 'src/providers/schedule/schedule';
import { CustomNavController } from 'src/shared/providers/navigation/custom-nav-controller';
import { RightSidePanelService } from 'src/shared/providers/providers/right-side-panel';
import { TidyStorage } from 'src/shared/providers/tidy-storage';

import { OwnerBlocksService } from 'src/shared/services/owner-blocks.service';
import { DemoBlockService } from 'src/shared/services/demo-block.service';

import { Loading } from 'src/shared/components/loading/loading';
import { TimeoutableComponent } from 'src/shared/components/timeoutable/timeoutable.component';
import { Util } from 'src/shared/util/util';

import { ParseDateTimePipe } from 'src/shared/pipes/parse-date.pipe';

// Where the schedule's block popover leaves the block to edit for this page.
export const OWNER_BLOCK_EDIT_KEY = 'owner-block-edit';

export interface OwnerBlockEditContext {
  addressId: number;
  startDate: string;
  endDate: string;
  reason: string | null;
}

@Component({
    templateUrl: 'add-owner-block.html',
    standalone: false
})
export class AddOwnerBlockPage extends TimeoutableComponent implements OnInit {

  // Matches the backend's limit on a block reason.
  static readonly REASON_MAX_LENGTH = 500;

  form: UntypedFormGroup;
  submitted = false;
  isRightSideContent = true;
  addressFilter: any;
  addressResponse: any;
  // Mobile pushes this as a full page, so it has to know which page to pop
  // back to — the schedule's + menu and the optimizer's "Block dates" both
  // open it.
  returnPage = 'schedule';
  previewRangeLabel = '';
  previewNightsLabel = '';
  // Set when the page was opened from a block's Edit action: it saves over
  // that block instead of adding a new one.
  editingBlock: OwnerBlockEditContext | null = null;

  boundBlockDates = this.blockDates.bind(this);

  private parseDateTimePipe = new ParseDateTimePipe();

  constructor(
    private client: Client,
    private navCtrl: CustomNavController,
    private ownerBlocks: OwnerBlocksService,
    private rightSidePanelService: RightSidePanelService,
    private schedule: Schedule,
    private storage: TidyStorage,
    private demoBlock: DemoBlockService,
    private utils: Util
  ) {
    super();
    this.form = new UntypedFormGroup({
      property: new UntypedFormControl(null, Validators.required),
      startDate: new UntypedFormControl(null, Validators.required),
      endDate: new UntypedFormControl(null, Validators.required),
      reason: new UntypedFormControl('', Validators.maxLength(AddOwnerBlockPage.REASON_MAX_LENGTH)),
    });
  }

  async ngOnInit() {
    this.isRightSideContent = (await this.storage.retrieve('dialog-right-side-open')) || false;
    this.loadDataAndParams();
  }

  @Loading('', true)
  async loadDataAndParams() {
    this.returnPage = (await this.storage.retrieve('owner-block-return-page')) || 'schedule';
    await this.storage.delete('owner-block-return-page');
    this.addressResponse = await this.client.getAddresses();
    this.addressFilter = this.client.parseAddressList(this.addressResponse, false);
    const preselectedProperty = await this.storage.retrieve('preselected-property');
    const storedAddress = await this.storage.retrieve('address');
    const property = preselectedProperty || storedAddress || (this.addressFilter?.length === 1 ? this.addressFilter[0].value : null);
    if (preselectedProperty) { await this.storage.delete('preselected-property'); }
    // A caller may prefill a range; the schedule passes a single day, which
    // prefills both ends.
    const preselectedDate = await this.storage.retrieve('preselected-date');
    const preselectedEndDate = await this.storage.retrieve('preselected-end-date');
    await this.storage.delete('preselected-end-date');
    if (preselectedDate) {
      await this.storage.delete('preselected-date');
      this.form.patchValue({ startDate: preselectedDate, endDate: preselectedEndDate || preselectedDate });
    }
    if (property) {
      this.form.patchValue({ property });
    }
    this.editingBlock = (await this.storage.retrieve(OWNER_BLOCK_EDIT_KEY)) || null;
    await this.storage.delete(OWNER_BLOCK_EDIT_KEY);
    if (this.editingBlock) {
      this.form.patchValue({
        property: this.editingBlock.addressId,
        startDate: this.editingBlock.startDate,
        endDate: this.editingBlock.endDate,
        reason: this.editingBlock.reason ?? '',
      });
    }
    this.updatePreview();
    this.loaded = true;
  }

  onDateChange(): void {
    const start = this.toIsoDate(this.form.value.startDate);
    const end = this.toIsoDate(this.form.value.endDate);
    if (start && (!end || end < start)) {
      this.form.patchValue({ endDate: this.form.value.startDate });
    }
    this.updatePreview();
  }

  updatePreview(): void {
    const start = this.toIsoDate(this.form.value.startDate);
    const end = this.toIsoDate(this.form.value.endDate);
    if (!start || !end || end < start) {
      this.previewRangeLabel = '';
      this.previewNightsLabel = '';
      return;
    }
    const nights = this.countNights(start, end);
    this.previewRangeLabel = this.buildRangeLabel(start, end);
    this.previewNightsLabel = nights === 1 ? '1 night' : `${nights} nights`;
  }

  async blockDates(): Promise<void> {
    this.submitted = true;
    if (!this.form.valid) {
      this.form.markAllAsTouched();
      return;
    }
    if (this.demoBlock.block()) { return; }
    // The property select is disabled while editing, which drops it from form.value.
    const addressId = this.editingBlock?.addressId ?? this.form.value.property;
    const start = this.toIsoDate(this.form.value.startDate);
    const end = this.toIsoDate(this.form.value.endDate);
    if (!start || !end || end < start) {
      this.utils.showError('Please enter a valid date range.');
      return;
    }
    const reason = (this.form.value.reason || '').trim();
    const loading = await this.utils.showLoading();
    try {
      if (this.editingBlock) {
        await this.ownerBlocks.updateRange(addressId, this.editingBlock, start, end, reason);
      } else {
        await this.ownerBlocks.blockRange(addressId, start, end, reason);
      }
      loading.dismiss();
      this.utils.showSuccess(this.editingBlock ? 'Blocked dates updated' : 'Dates blocked');
      localStorage.setItem('madeUpdateToSchedule', 'true');
      if (this.isRightSideContent) {
        // Closing the panel triggers the schedule's afterPanelClosed reload,
        // which picks up the flag — no extra reload signal needed.
        this.rightSidePanelService.closeRightSidePanel();
      } else {
        this.schedule.setReloadSchedulePage();
        this.navCtrl.navigateBack(this.returnPage);
      }
    } catch (_error) {
      this.submitted = false;
      loading.dismiss();
      this.utils.showError(this.editingBlock
        ? 'Error updating these dates, please try again.'
        : 'Error blocking these dates, please try again.');
    }
  }

  private buildRangeLabel(start: string, end: string): string {
    if (start === end) {
      return this.parseDateTimePipe.transform(start, 'MMM d');
    }
    return `${this.parseDateTimePipe.transform(start, 'MMM d')} - ${this.parseDateTimePipe.transform(end, 'MMM d')}`;
  }

  private countNights(start: string, end: string): number {
    return Math.round(LuxonDateTime.fromISO(end).diff(LuxonDateTime.fromISO(start), 'days').days) + 1;
  }

  private toIsoDate(value: Date | string | null): string | null {
    if (!value) { return null; }
    if (value instanceof Date) {
      return LuxonDateTime.fromJSDate(value).toISODate();
    }
    const parsed = LuxonDateTime.fromISO(String(value));
    return parsed.isValid ? parsed.toISODate() : null;
  }
}
